/**
 * Money in any currency, one module for the server, the web, the app and the
 * worker: each currency's minor unit, minor ↔ decimal conversion, the
 * conventions some processors and stores use instead, conversion at a rate
 * and cross rates (and the estimate in a fan's own currency), formatting in
 * a locale, each country's currency, and reading an amount as a seller's
 * page shows it. What a fan reads in Lissen's own house style (the store's
 * currency, and the estimate) is `storeMoney`'s, in store-money.ts.
 *
 * Every vendor `*Minor` amount on the wire and in the database (`priceMinor`,
 * `totalMinor`, `feeMinor`, `checkouts.total_minor` ...) is a whole number of
 * its currency's ISO 4217 minor unit: 4500 is £45.00, ¥4,500 and BHD 4.500.
 * Engine amounts retain their separate Intl-based convention.
 *
 * **The exponent source is ISO 4217 itself**: List One as SIX Financial
 * Information (the maintenance agency) published it on 17 Sep 2026, copied
 * below. Not Intl's `maximumFractionDigits`, which is CLDR's display
 * convention: it drops subunits nobody pays in cash (Node 24's CLDR 47 gives
 * 0 for AFN, ALL, IQD, IRR, KPW, LAK, LBP, MGA, MMK, RSD, SOS, SYP and YER,
 * where ISO says 2, or 3 for IQD), and it is whatever data the runtime ships,
 * so Node, a browser and Hermes on a phone could disagree about what one
 * `priceMinor` means. A table in the code answers the same everywhere, and
 * the processors define their own conventions as departures from ISO. To
 * update it, read the new list's `Ccy` and `CcyMnrUnts` and keep the
 * published date here; a currency whose minor unit changes needs its stored
 * amounts migrated with it.
 *
 * **Processor conventions that differ from ISO** are read and written at the
 * boundary with that processor or store (`fromAmountConvention`,
 * `toAmountConvention`), never stored:
 * - Stripe (Payment Links' `unit_amount`): ISK and UGX "transitioned to a
 *   zero-decimal currency, but backward compatibility requires you to
 *   represent it as a two-decimal value, where the decimal amount is always
 *   00" (5 ISK is 500), and MGA is in its zero-decimal list though ISO gives
 *   it 2 (docs.stripe.com/currencies, read 19 Oct 2026). HUF and TWD charge
 *   with ISO's two decimals; Stripe only pays them out in whole units, which
 *   Lissen never does, so they read as ISO.
 * - A hundredth of the unit in every currency (`hundredths`), as Shopify's
 *   Liquid and Ajax prices are: "for currencies without subunits, such as JPY
 *   and KRW, tenths and hundredths of a unit are appended" (¥1,000 is 100000).
 */

/** ISO 4217 List One, published 17 Sep 2026: every currency with a minor
 *  unit, by its exponent. The funds and metals with none (XAU, XDR, XXX ...)
 *  are left out. */
const ISO_4217_BY_EXPONENT: Readonly<Record<number, string>> = {
  0: "BIF CLP DJF GNF ISK JPY KMF KRW PYG RWF UGX UYI VND VUV XAF XOF XPF",
  2:
    "AED AFN ALL AMD AOA ARS AUD AWG AZN BAM BBD BDT BMD BND BOB BOV " +
    "BRL BSD BTN BWP BYN BZD CAD CDF CHE CHF CHW CNY COP COU CRC CUP " +
    "CVE CZK DKK DOP DZD EGP ERN ETB EUR FJD FKP GBP GEL GHS GIP GMD " +
    "GTQ GYD HKD HNL HTG HUF IDR ILS INR IRR JMD KES KGS KHR KPW KYD " +
    "KZT LAK LBP LKR LRD LSL MAD MDL MGA MKD MMK MNT MOP MRU MUR MVR " +
    "MWK MXN MXV MYR MZN NAD NGN NIO NOK NPR NZD PAB PEN PGK PHP PKR " +
    "PLN QAR RON RSD RUB SAR SBD SCR SDG SEK SGD SHP SLE SOS SRD SSP " +
    "STN SVC SYP SZL THB TJS TMT TOP TRY TTD TWD TZS UAH USD USN UYU " +
    "UZS VED VES WST XAD XCD XCG YER ZAR ZMW ZWG",
  3: "BHD IQD JOD KWD LYD OMR TND",
  4: "CLF UYW",
};

const ISO_4217 = new Map(
  Object.entries(ISO_4217_BY_EXPONENT).flatMap(([exponent, codes]) =>
    codes.split(" ").map((code) => [code, Number(exponent)] as const),
  ),
);

/** Whether `currency` is an ISO 4217 code with a minor unit ("GBP", not
 *  "gbp", "XAU" or "ABC"). */
export const isIsoCurrency = (currency: string) => ISO_4217.has(currency);

/** How many decimals `currency`'s minor unit is: 2 for GBP, 0 for JPY, 3 for
 *  BHD (ISO 4217). Throws for a code ISO does not list with a minor unit. */
export function currencyExponent(currency: string): number {
  const exponent = ISO_4217.get(currency);
  if (exponent === undefined)
    throw new RangeError(`${currency} is not an ISO 4217 currency with a minor unit.`);
  return exponent;
}

/** The decimals display writes `currency` with: ISO's exponent, or two (as
 *  most currencies have, and as every display did before) for a code ISO
 *  does not list, so a page never fails on a store's odd code. */
export const displayDecimals = (currency: string) =>
  ISO_4217.get(currency.toUpperCase()) ?? 2;

function wholeMinor(minor: number): number {
  if (!Number.isSafeInteger(minor))
    throw new RangeError(`${minor} is not a whole number of minor units.`);
  return minor;
}

/** Exact decimal text of `minor` minor units of `currency`: 4500 GBP is
 *  "45.00", 4500 JPY "4500", 1234 BHD "1.234", -50 GBP "-0.50". */
export function minorToDecimal(minor: number, currency: string): string {
  const exponent = currencyExponent(currency);
  const digits = String(Math.abs(wholeMinor(minor))).padStart(exponent + 1, "0");
  const sign = minor < 0 ? "-" : "";
  return exponent
    ? `${sign}${digits.slice(0, -exponent)}.${digits.slice(-exponent)}`
    : `${sign}${digits}`;
}

/** Decimal text ("45", "45.5", "45.00", "4500.0") in minor units of
 *  `currency`, exactly, on the digits. Throws for anything else, or for a
 *  fraction finer than the currency's minor unit ("45.001" GBP). */
export function decimalToMinor(text: string, currency: string): number {
  const exponent = currencyExponent(currency);
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(text.trim());
  if (!match) throw new RangeError(`"${text}" is not a decimal amount.`);
  const fraction = (match[3] ?? "").replace(/0+$/, "");
  if (fraction.length > exponent)
    throw new RangeError(`"${text}" is finer than ${currency}'s minor unit.`);
  const minor = Number(`${match[2]}${fraction.padEnd(exponent, "0")}`);
  wholeMinor(minor);
  return match[1] && minor ? -minor : minor;
}

/** `minor` in major units as a number (4500 GBP is 45, 4500 JPY 4500), for
 *  the wire's major-unit amounts (a page's `price.amount`) and display. */
export const minorToMajor = (minor: number, currency: string) =>
  minor / 10 ** displayDecimals(currency);

/** An amount in major units as a number (a vendor's JSON `12.5`), to the
 *  nearest minor unit of `currency`, ties towards positive infinity as in
 *  Math.round. Scale its decimal digits so 1.005 GBP rounds to 101 pence. */
export function majorToMinor(major: number, currency: string): number {
  const exponent = currencyExponent(currency);
  if (!Number.isFinite(major)) throw new RangeError(`${major} is not a finite amount.`);
  const [coefficient, power = "0"] = String(Math.abs(major)).split("e");
  const [whole, fraction = ""] = coefficient!.split(".");
  const digits = BigInt(`${whole}${fraction}`);
  const shift = exponent + Number(power) - fraction.length;
  let minor: bigint;
  if (shift >= 0) minor = digits * 10n ** BigInt(shift);
  else {
    const scale = 10n ** BigInt(-shift);
    const remainder = (digits % scale) * 2n;
    const roundUp = major < 0 ? remainder > scale : remainder >= scale;
    minor = digits / scale + (roundUp ? 1n : 0n);
  }
  return wholeMinor(Number(major < 0 ? -minor : minor));
}

/**
 * How a processor or store writes an amount as a whole number:
 * - `iso4217`: ISO's minor unit, as vendor amounts are stored;
 * - `stripe`: Stripe's, which is ISO's except ISK and UGX (two decimals,
 *   always 00) and MGA (none);
 * - `hundredths`: a hundredth of the unit in every currency (Shopify's
 *   Liquid and Ajax prices).
 */
export type AmountConvention = "iso4217" | "stripe" | "hundredths";

const STRIPE_EXPONENTS: Readonly<Record<string, number>> = { ISK: 2, UGX: 2, MGA: 0 };

function conventionExponent(currency: string, convention: AmountConvention) {
  const iso = currencyExponent(currency);
  if (convention === "hundredths") return 2;
  if (convention === "stripe") return STRIPE_EXPONENTS[currency] ?? iso;
  return iso;
}

function rescale(amount: number, from: number, to: number): number {
  wholeMinor(amount);
  if (to >= from) return wholeMinor(amount * 10 ** (to - from));
  const scale = 10 ** (from - to);
  if (amount % scale !== 0)
    throw new RangeError(`${amount} is finer than the currency's minor unit.`);
  return amount / scale;
}

/** An amount a processor or store wrote in its `convention`, in ISO minor
 *  units: Stripe's 500 ISK is 5, Shopify's 450000 JPY is 4500. Throws when
 *  it holds a fraction of the minor unit. */
export const fromAmountConvention = (
  amount: number,
  currency: string,
  convention: AmountConvention,
) => rescale(amount, conventionExponent(currency, convention), currencyExponent(currency));

/** ISO minor units in a processor's or store's `convention`. */
export const toAmountConvention = (
  minor: number,
  currency: string,
  convention: AmountConvention,
) => rescale(minor, currencyExponent(currency), conventionExponent(currency, convention));

/**
 * `minor` minor units of `from` in minor units of `to`, at `rate` (units of
 * `to` for one unit of `from`, as decimal text: Open Exchange Rates' "0.75"
 * pounds per dollar), to the nearest minor unit, halves up. Exact: whole
 * number arithmetic on the rate's digits and both currencies' exponents.
 * Throws for a negative or fractional amount or a rate that is not decimal
 * text.
 */
export function convertMinor(
  minor: number,
  { from, to, rate }: { from: string; to: string; rate: string },
): number {
  return atRate(minor, rate, currencyExponent(from), currencyExponent(to));
}

/** Decimal text ("0.75", "1", "190.50000000") as its digits and how many of
 *  them are decimals: 0.75 is 75 and 2. Throws for anything else. */
function decimalDigits(text: string): { digits: bigint; decimals: number } {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(text);
  if (!match) throw new RangeError(`"${text}" is not a rate.`);
  const fraction = match[2] ?? "";
  return { digits: BigInt(`${match[1]}${fraction}`), decimals: fraction.length };
}

/** `minor` units of `10^-fromExponent` at `rate`, in units of
 *  `10^-toExponent`, to the nearest, halves up: `convertMinor`'s exact
 *  arithmetic, which `approxWholeUnits` uses for whole units. */
function atRate(minor: number, rate: string, fromExponent: number, toExponent: number): number {
  const { digits, decimals } = decimalDigits(rate);
  if (wholeMinor(minor) < 0) throw new RangeError(`${minor} is negative.`);
  const numerator = BigInt(minor) * digits * 10n ** BigInt(toExponent);
  const denominator = 10n ** BigInt(decimals + fromExponent);
  return wholeMinor(Number((numerator + denominator / 2n) / denominator));
}

/**
 * `dividend` divided by `divisor`, both decimal text, as decimal text with
 * exactly `places` decimals, halves up: "0.75" / "0.92" to 8 places is
 * "0.81521739". Exact, on the digits; no floating point. A cross rate
 * through a common base is one: pounds per dollar over euros per dollar is
 * pounds per euro. Throws for text that is not a non-negative decimal, and
 * for a divisor of zero.
 */
export function divideDecimal(dividend: string, divisor: string, places: number): string {
  if (!Number.isSafeInteger(places) || places < 0)
    throw new RangeError(`${places} is not a number of decimal places.`);
  const a = decimalDigits(dividend);
  const b = decimalDigits(divisor);
  if (b.digits === 0n) throw new RangeError(`"${divisor}" is zero.`);
  // a/b = (A / 10^ea) / (B / 10^eb); scaled by 10^places.
  const numerator = a.digits * 10n ** BigInt(b.decimals + places);
  const denominator = b.digits * 10n ** BigInt(a.decimals);
  const scaled = String((numerator * 2n + denominator) / (denominator * 2n)).padStart(places + 1, "0");
  return places ? `${scaled.slice(0, -places)}.${scaled.slice(-places)}` : scaled;
}

/**
 * `minor` minor units of `currency` as `locale` writes money, with the
 * currency's own decimals: "£45.00", "US$45.00", "JP¥4,500" and "BHD 1.234"
 * in en-GB, "45,00 €" in de-DE. A code Intl refuses is written after the
 * amount ("45.00 ABC"). Not what a fan reads of a listing or an order: the
 * app and Link in Bio say those in Lissen's house style (`storeMoney`, in
 * store-money.ts: "NZD 65.00", not "NZ$65.00").
 */
export const formatMinor = (minor: number, currency: string, locale = "en-GB") =>
  formatMajor(minorToMajor(minor, currency), currency, locale);

/** `formatMinor` for an amount in major units (45 for £45.00, a stored
 *  order's `amount_total`). */
export function formatMajor(amount: number, currency: string, locale = "en-GB"): string {
  const code = currency.toUpperCase();
  return formatWith(amount, code, displayDecimals(code), locale);
}

function formatWith(amount: number, code: string, decimals: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(amount);
  } catch {
    return `${amount.toFixed(decimals)} ${code}`;
  }
}

/** A journey's estimate in the fan's own currency (`approx`): `rate` units
 *  of `currency` for one unit of the listing's, as decimal text. */
export type FanEstimateRate = { currency: string; rate: string };

/**
 * `minor` minor units of `currency` (a listing's) in whole units of the
 * fan's own currency at the journey's `approx` rate, to the nearest, halves
 * up, as `convertMinor`: €75.00 at 0.84612 pounds a euro is 63, ¥4,500 at
 * 0.0052 is 23. The amount behind "about £63" (`formatApprox`, in
 * store-money.ts). Throws for a rate that is not decimal text.
 */
export const approxWholeUnits = (minor: number, currency: string, approx: FanEstimateRate) =>
  atRate(minor, approx.rate, currencyExponent(currency), 0);

// The currency of a country.

/**
 * Each country's currency, by currency: CLDR's current tender for each
 * region (`supplemental/currencyData.json`, `region`: the first currency
 * with no end date that is legal tender), as CLDR 48.2.3 published it on
 * 30 Sep 2026 (github.com/unicode-org/cldr-json, tag 48.2.3; sha256
 * e50315091b1f34428480f82970be64159ad207af0bdcebe7faa6260d3e34c8a6), for
 * every country `isIsoCountryCode` (`@libs/utils/isoCountries`) knows, ISO
 * 3166-1 alpha-2 and XK. Antarctica (AQ) has none. Where a country has two,
 * CLDR's first is the one ICU gives it too (ZAR in Lesotho,
 * PAB in Panama). Not Intl: no API gives a region's currency, and a table
 * answers the same on the server, the web and the app. To update it, take
 * a new release's file, keep the same rule and record its version here.
 */
const COUNTRIES_BY_CURRENCY: Readonly<Record<string, string>> = {
  AED: "AE", AFN: "AF", ALL: "AL", AMD: "AM", AOA: "AO", ARS: "AR",
  AUD: "AU CC CX HM KI NF NR TV", AWG: "AW", AZN: "AZ", BAM: "BA", BBD: "BB",
  BDT: "BD", BHD: "BH", BIF: "BI", BMD: "BM", BND: "BN", BOB: "BO", BRL: "BR",
  BSD: "BS", BTN: "BT", BWP: "BW", BYN: "BY", BZD: "BZ", CAD: "CA", CDF: "CD",
  CHF: "CH LI", CLP: "CL", CNY: "CN", COP: "CO", CRC: "CR", CUP: "CU",
  CVE: "CV", CZK: "CZ", DJF: "DJ", DKK: "DK FO GL", DOP: "DO", DZD: "DZ",
  EGP: "EG", ERN: "ER", ETB: "ET",
  EUR:
    "AD AT AX BE BG BL CY DE EE ES FI FR GF GP GR HR IE IT LT LU LV MC " +
    "ME MF MQ MT NL PM PT RE SI SK SM TF VA XK YT",
  FJD: "FJ", FKP: "FK", GBP: "GB GG GS IM JE", GEL: "GE", GHS: "GH", GIP: "GI",
  GMD: "GM", GNF: "GN", GTQ: "GT", GYD: "GY", HKD: "HK", HNL: "HN", HTG: "HT",
  HUF: "HU", IDR: "ID", ILS: "IL PS", INR: "IN", IQD: "IQ", IRR: "IR",
  ISK: "IS", JMD: "JM", JOD: "JO", JPY: "JP", KES: "KE", KGS: "KG", KHR: "KH",
  KMF: "KM", KPW: "KP", KRW: "KR", KWD: "KW", KYD: "KY", KZT: "KZ", LAK: "LA",
  LBP: "LB", LKR: "LK", LRD: "LR", LYD: "LY", MAD: "EH MA", MDL: "MD",
  MGA: "MG", MKD: "MK", MMK: "MM", MNT: "MN", MOP: "MO", MRU: "MR", MUR: "MU",
  MVR: "MV", MWK: "MW", MXN: "MX", MYR: "MY", MZN: "MZ", NAD: "NA", NGN: "NG",
  NIO: "NI", NOK: "BV NO SJ", NPR: "NP", NZD: "CK NU NZ PN TK", OMR: "OM",
  PAB: "PA", PEN: "PE", PGK: "PG", PHP: "PH", PKR: "PK", PLN: "PL", PYG: "PY",
  QAR: "QA", RON: "RO", RSD: "RS", RUB: "RU", RWF: "RW", SAR: "SA", SBD: "SB",
  SCR: "SC", SDG: "SD", SEK: "SE", SGD: "SG", SHP: "SH", SLE: "SL", SOS: "SO",
  SRD: "SR", SSP: "SS", STN: "ST", SYP: "SY", SZL: "SZ", THB: "TH", TJS: "TJ",
  TMT: "TM", TND: "TN", TOP: "TO", TRY: "TR", TTD: "TT", TWD: "TW", TZS: "TZ",
  UAH: "UA", UGX: "UG",
  USD: "AS BQ EC FM GU IO MH MP PR PW SV TC TL UM US VG VI", UYU: "UY",
  UZS: "UZ", VES: "VE", VND: "VN", VUV: "VU", WST: "WS",
  XAF: "CF CG CM GA GQ TD", XCD: "AG AI DM GD KN LC MS VC", XCG: "CW SX",
  XOF: "BF BJ CI GW ML NE SN TG", XPF: "NC PF WF", YER: "YE", ZAR: "LS ZA",
  ZMW: "ZM", ZWG: "ZW",
};

const CURRENCY_OF_COUNTRY = new Map(
  Object.entries(COUNTRIES_BY_CURRENCY).flatMap(([currency, countries]) =>
    countries.split(" ").map((country) => [country, currency] as const),
  ),
);

/** The currency of `country` (ISO 3166-1 alpha-2, in capitals): "GBP" for
 *  GB, "EUR" for IE, "USD" for PR. Null for a code that names no country
 *  with a currency. A fan's own currency is their fan country's. */
export const countryCurrency = (country: string): string | null =>
  CURRENCY_OF_COUNTRY.get(country) ?? null;

// Reading an amount as a seller shows it.

/** Where an amount is read: the currency it is in and the locale its page
 *  writes numbers in ("en-GB": 1,234.56; "de-DE": 1.234,56; "fr-FR":
 *  1 234,56). */
export type SellerMoney = { currency: string; locale: string };

/** Lissen's own names for dollars beside pounds (`storeMoney`), so what Lissen
 *  wrote reads back. */
export const HOUSE_CURRENCY_PREFIXES: Readonly<Record<string, string>> = {
  USD: "US$",
  CAD: "CAD$",
  AUD: "AUS$",
};

const escapeRegExp = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Spaces of every width as one plain space (line breaks kept, as one
 *  "\n"), digits of any script as ASCII, and direction marks gone, so a
 *  page's text compares with Intl's. */
function normalise(text: string): string {
  return text
    .replace(/[\u200e\u200f\u061c]/g, "")
    .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[\uff10-\uff19]/g, (d) => String(d.charCodeAt(0) - 0xff10))
    .replace(/\s*[\r\n]\s*/g, "\n")
    .replace(/[^\S\n]+/g, " ")
    .replace(/[\u2019']/g, "'");
}

/** The signs and codes `currency` is shown with: its ISO code, Intl's symbol
 *  and narrow symbol in the page's locale and in English ("£", "US$", "$",
 *  "CA$", "€", "¥", "￥", "kr"), and Lissen's own prefix. Longest first, so
 *  "US$" is read before "$". */
function currencyMarkers(currency: string, locale: string): string[] {
  const markers = new Set([currency]);
  for (const where of [locale, "en-GB", "en"])
    for (const currencyDisplay of ["symbol", "narrowSymbol"] as const) {
      try {
        const sign = new Intl.NumberFormat(where, { style: "currency", currency, currencyDisplay })
          .formatToParts(1)
          .find((part) => part.type === "currency")?.value;
        if (sign) markers.add(normalise(sign));
      } catch {
        // A locale or code Intl does not know adds no sign.
      }
    }
  const house = HOUSE_CURRENCY_PREFIXES[currency];
  if (house) markers.add(house);
  return [...markers].sort((a, b) => b.length - a.length);
}

/** The signs and codes `where.currency` is shown with on a page ("£",
 *  "GBP"; "US$", "$", "USD"), longest first: what a page's own script
 *  can look for where it cannot read an amount itself. */
export const currencySigns = ({ currency, locale }: SellerMoney): string[] =>
  currencyMarkers(currency, locale);

/** The group and decimal separators of `locale`'s numbers. */
function separators(locale: string): { group: string; decimal: string } {
  const parts = new Intl.NumberFormat(locale, { minimumFractionDigits: 1 }).formatToParts(
    1234567.5,
  );
  return {
    group: normalise(parts.find((part) => part.type === "group")?.value ?? ","),
    decimal: normalise(parts.find((part) => part.type === "decimal")?.value ?? "."),
  };
}

/** Whether `locale` writes `currency`'s sign before its amount ("£1.00",
 *  "￥1") rather than after it ("1,00 €"). */
function signFirst(currency: string, locale: string): boolean {
  try {
    const parts = new Intl.NumberFormat(locale, { style: "currency", currency }).formatToParts(1);
    return parts.findIndex((part) => part.type === "currency") < parts.findIndex((part) => part.type === "integer");
  } catch {
    return true;
  }
}

type Reader = {
  currency: string;
  exponent: number;
  group: string;
  decimal: string;
  markers: string[];
  signFirst: boolean;
};

function readerFor({ currency, locale }: SellerMoney): Reader {
  return {
    currency,
    exponent: currencyExponent(currency),
    ...separators(locale),
    markers: currencyMarkers(currency, locale),
    signFirst: signFirst(currency, locale),
  };
}

/** A number as the locale writes it ("1,234.56", "1.234,56", "1 234,56",
 *  "1234", "12,34,567.89" in India), in minor units; null when it is not one
 *  or is finer than the currency's minor unit. */
function readNumber(text: string, reader: Reader): number | null {
  const group = escapeRegExp(reader.group);
  const decimal = escapeRegExp(reader.decimal);
  const match = new RegExp(
    `^(\\d{1,3}(?:${group}\\d{3})+|\\d{1,2}(?:${group}\\d{2})+${group}\\d{3}|\\d+)(?:${decimal}(\\d+))?$`,
  ).exec(text);
  if (!match) return null;
  const fraction = (match[2] ?? "").replace(/0+$/, "");
  if (fraction.length > reader.exponent) return null;
  const minor = Number(
    `${match[1]!.split(reader.group).join("")}${fraction.padEnd(reader.exponent, "0")}`,
  );
  return Number.isSafeInteger(minor) ? minor : null;
}

const MINUS = /^[-\u2212]\s?/;

/**
 * An amount as a seller's page shows it, the whole of `text`, in minor units
 * of `where.currency`: "£12.50", "€1.234,56" (de-DE), "1 234,56 €" (fr-FR),
 * "US$12.99", "CA$5", "¥4,500" (en-GB), "12.50 GBP", or the bare number
 * ("12.50"). Its sign or code, when it has one, must be the currency's: "$5"
 * is no amount in pounds. Null for anything else, including a number finer
 * than the currency's minor unit or written in another locale's separators
 * ("1.234,56" in en-GB).
 */
export function parseAmount(text: string, where: SellerMoney): number | null {
  const reader = readerFor(where);
  let rest = normalise(text).trim();
  let negative = false;
  if (MINUS.test(rest)) {
    negative = true;
    rest = rest.replace(MINUS, "");
  }
  const marker = reader.markers.find(
    (sign) => rest.startsWith(sign) || rest.endsWith(sign),
  );
  if (marker)
    rest = (rest.startsWith(marker) ? rest.slice(marker.length) : rest.slice(0, -marker.length)).trim();
  if (!negative && MINUS.test(rest)) {
    negative = true;
    rest = rest.replace(MINUS, "");
  }
  const minor = readNumber(rest, reader);
  return minor === null ? null : negative && minor ? -minor : minor;
}

/** An amount found in a page's text (`normalise`d): its minor units, and
 *  where it starts and ends, its sign or code and its minus included. */
type Found = { minor: number; start: number; end: number };

/** Every amount in the reader's currency `text` (`normalise`d) shows beside
 *  its sign or code, in order. */
function scanAmounts(text: string, reader: Reader): Found[] {
  const signs = reader.markers.map(escapeRegExp).join("|");
  // Consume foreign separators too: validating the whole token must reject
  // "€12.50" in French, rather than finding just "€12" or "50 €".
  const separator = `[.,'\u066b\u066c${escapeRegExp(reader.group)}${escapeRegExp(reader.decimal)}]`;
  const number = `\\d+(?:${separator}\\d+)*`;
  // A code or a sign stands apart from a Latin word, so "kr" and "CHF" are
  // not read inside one, nor "$" in "CA$" or "¥" in "CN¥".
  // A minus is the minus sign, or a hyphen against the amount ("-£5.00"):
  // a spaced hyphen separates words ("Standing - £12.00").
  const minus = `(\u2212 ?|-)`;
  // A sign between two numbers ("Tier 1 \u00a312.00", "Tier 1\n\u00a312.00") is the
  // later one's when it touches it, or when the locale writes signs first;
  // else the earlier one's ("12,50 \u20ac 3 places").
  const ownedByNext = reader.signFirst ? `\\s?(?:\u2212 ?|-)?\\d` : `(?:\u2212 ?|-)?\\d`;
  const pattern = new RegExp(
    `(?<![A-Za-z])${minus}?(?:${signs})\\s?${minus}?(${number})|${minus}?(${number})\\s?(?:${signs})(?![A-Za-z])(?!${ownedByNext})`,
    "gu",
  );
  const found: Found[] = [];
  for (const match of text.matchAll(pattern)) {
    const prefixed = match[3] !== undefined;
    if (prefixed && match[1] && match[2]) continue;
    const negative = !!(prefixed ? match[1] || match[2] : match[4]);
    const digits = (prefixed ? match[3] : match[5])!;
    const end = match.index + match[0].length;
    // "2 12,50 €" where a space groups thousands: the amount is its longest
    // tail that is a number ("12,50").
    const tails = prefixed || reader.group !== " "
      ? [digits]
      : digits.split(reader.group).map((_, index, chunks) => chunks.slice(index).join(reader.group));
    for (const tail of tails) {
      const minor = readNumber(tail, reader);
      if (minor === null) continue;
      const start = tail === digits
        ? match.index
        : match.index + match[0].indexOf(digits) + digits.length - tail.length;
      found.push({ minor: negative && minor ? -minor : minor, start, end });
      break;
    }
  }
  return found;
}

/**
 * Every amount in `where.currency` a longer text shows beside its sign or
 * code ("Total: £12.50 inc. VAT", "2 × 12,50 € = 25,00 €"), in minor units,
 * in the order they appear. A number without the currency's sign or code is
 * not money here (a quantity, a date), and neither is one in another currency.
 */
export const findAmounts = (text: string, where: SellerMoney): number[] =>
  scanAmounts(normalise(text), readerFor(where)).map((found) => found.minor);

/**
 * The amount in `where.currency` a page's text shows right after `label`,
 * with nothing but space between ("TOTAL £9.60", "Total\n£13.50",
 * "Gesamt 1.234,56 €"), in minor units; null when no match of `label` is
 * followed by one. `label` is matched against the text with its spaces
 * collapsed to one and its line breaks kept ("(?:^|\n) ?Total" finds a line
 * that starts with Total).
 */
export function amountAfter(text: string, label: RegExp, where: SellerMoney): number | null {
  const normalised = normalise(text);
  const found = scanAmounts(normalised, readerFor(where));
  const flags = label.flags.includes("g") ? label.flags : `${label.flags}g`;
  for (const match of normalised.matchAll(new RegExp(label.source, flags))) {
    const after = match.index + match[0].length;
    const gap = /^\s*/.exec(normalised.slice(after))![0].length;
    const amount = found.find((candidate) => candidate.start === after + gap);
    if (amount) return amount.minor;
  }
  return null;
}

/** The amount in `where.currency` a page's text shows right before `label`,
 *  with nothing but space between ("£9.00 face value"), in minor units; null
 *  when no match of `label` follows one. `label` is matched as for
 *  `amountAfter`. */
export function amountBefore(text: string, label: RegExp, where: SellerMoney): number | null {
  const normalised = normalise(text);
  const found = scanAmounts(normalised, readerFor(where));
  const flags = label.flags.includes("g") ? label.flags : `${label.flags}g`;
  for (const match of normalised.matchAll(new RegExp(label.source, flags))) {
    const gap = /\s*$/.exec(normalised.slice(0, match.index))![0].length;
    const amount = found.find((candidate) => candidate.end === match.index - gap);
    if (amount) return amount.minor;
  }
  return null;
}

/** `text` without the amounts in `where.currency` it shows, each with its
 *  sign or code ("E-ticket (£1.50)" is "E-ticket ()"), its spaces collapsed
 *  as `amountAfter` reads them. */
export function withoutAmounts(text: string, where: SellerMoney): string {
  const normalised = normalise(text);
  let rest = "";
  let from = 0;
  for (const amount of scanAmounts(normalised, readerFor(where))) {
    rest += normalised.slice(from, amount.start);
    from = amount.end;
  }
  return rest + normalised.slice(from);
}

/** `text` without the amount in `where.currency` that ends it ("Entry Before
 *  11.30pm £4.00" is "Entry Before 11.30pm"), keeping any other amount in it
 *  ("£5 Early Bird £5.00" is "£5 Early Bird"), its spaces collapsed as
 *  `amountAfter` reads them and trimmed at its end. */
export function withoutTrailingAmount(text: string, where: SellerMoney): string {
  const normalised = normalise(text).trimEnd();
  const last = scanAmounts(normalised, readerFor(where)).at(-1);
  return last?.end === normalised.length ? normalised.slice(0, last.start).trimEnd() : normalised;
}

/** Whether a page's text shows `minor` minor units of `where.currency` as an
 *  amount ("find £12.50 on the page"). */
export const showsAmount = (text: string, minor: number, where: SellerMoney) =>
  findAmounts(text, where).includes(minor);
